/**
 * Карточка бренда: чем он для нас является и по чему считается его видимость.
 *
 * Три вкладки — сам бренд, синонимы названия и конкуренты — потому что это три
 * разные сущности спеки §5, и правятся они по-разному. Роль, которая ничего не
 * меняет, видит те же вкладки без форм: чего роль не может, того на экране нет,
 * а не серым (app-kit.md §1).
 *
 * Смена домена помечается на ленте событий бренда — контракт делает это сам, а
 * экран предупреждает заранее: без такой отметки падение видимости после
 * переезда сайта выглядит как вина клиента.
 */

import { useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';

import { api } from '../../api/client';
import { ApiError, fieldErrors } from '../../api/problem';
import { useAccess } from '../../session';
import { useScope } from '../../scope';
import { useLoad } from '../../ui/load';
import { normalizeDomain } from '@seeno/shared/domain';
import { Chips, chipsValue, Confirm, TextField } from '../../ui/fields';
import { Tabs, TabPanel } from '../../ui/Tabs';
import { Empty, ErrorNotice, Skeleton } from '../../ui/state';
import { brandStatus } from '../../ui/labels';
import { Aliases } from './Aliases';
import { Competitors } from './Competitors';
import {
  dropRemovedBrand,
  removeBrand,
  RemoveConfirmBody,
  useStillHere,
} from './remove-brand';
import { countOf, dateProse, domainProse } from '@shared/src/format';
import type { components } from '../../api/schema';

type Brand = components['schemas']['Brand'];

type Tab = 'brand' | 'aliases' | 'competitors';

/**
 * Какие действия карточка предлагает.
 *
 * Решение вынесено функцией, потому что оно про две разные вещи сразу — права
 * роли и состояние бренда, — и обе легко перепутать. Кнопки «Открыть обзор» и
 * «К замерам» ВЫБИРАЮТ бренд областью работы, а архивный выбрать нельзя: раньше
 * они возвращали его в переключатель, из которого он только что ушёл. Право на
 * архивацию спрашивается у своего пути `POST /brands/{brandId}/archive`, а не у
 * `PATCH /brands/{brandId}`: правки карточки и вывод бренда из работы — разные
 * операции, и однажды они разойдутся по ролям.
 *
 * Удаление — единственный контрол БЕЗ проверки состояния, и это не забытое
 * условие. Убрать из кабинета можно и живой бренд, и архивный: живой сервер
 * сначала архивирует сам, теми же правилами и в той же транзакции. Приписать
 * сюда `live &&` значит запереть в архиве всё, что туда уже уехало, — то есть
 * ровно те бренды, ради которых удаление и просили.
 */
export function brandCardControls(
  status: Brand['status'],
  reaches: (method: string, path: string) => boolean,
): { overview: boolean; runs: boolean; archive: boolean; remove: boolean } {
  const live = status !== 'archived';
  return {
    overview: live,
    runs: live && reaches('POST', '/runs'),
    archive: live && reaches('POST', '/brands/{brandId}/archive'),
    remove: reaches('POST', '/brands/{brandId}/hide'),
  };
}

/**
 * Отказ, за которым бренд всё-таки в архиве.
 *
 * Наверх из клиента API приходит только `ApiError`: не-JSON от края CDN, обрыв
 * сети и пустое тело завёрнуты в него же, а `status` берётся из HTTP-ответа, а
 * не из тела (`readProblem` в `api/client.ts`). Поэтому вопрос не в том, как
 * ошибка обёрнута, — вопрос в том, что означает 409.
 *
 * Сегодня на этой ручке он означает «уже заархивирован», но завтра может
 * означать «по бренду идёт замер», и сказать на такой отказ «он уже был там» —
 * соврать второй раз в том же экране. Поэтому решает не форма ошибки, а
 * перечитанная карточка: бренд в архиве — человек получил ровно то, чего хотел,
 * кем бы архивация ни была сделана. Не перечиталась — показываем ответ сервера.
 */
export function conflictMeansArchived(cause: unknown, fresh: Brand | null): boolean {
  if (!(cause instanceof ApiError) || cause.problem.status !== 409) return false;
  return fresh?.status === 'archived';
}

/**
 * Текст согласия на архивацию.
 *
 * Отдельным экспортом, потому что его стережёт приёмка: здесь стояло «архив это
 * пауза, а не удаление», и это было неправдой — возврата из архива в продукте
 * нет. Обещание обратимости в диалоге необратимого действия — худший вид
 * копирайта, какой бывает: человек соглашается не на то, что произойдёт.
 */
export function ArchiveConfirmBody({
  name,
  questions,
}: {
  name: string;
  questions: number;
}) {
  return (
    <>
      <p className="dlg-lead">
        Замеры по «{name}» остановятся,{' '}
        {countOf(questions, 'вопрос', 'вопроса', 'вопросов')} освободят квоту тарифа, а
        название снова можно будет занять. Собранная история останется целиком: карточка,
        отчёты и прошлые замеры открываются как раньше.
      </p>
      <p className="dlg-lead">
        Вернуть бренд из архива нельзя. Он уйдёт из переключателя и рабочих списков, а
        заведённый заново начнёт замеры с чистого листа.
      </p>
    </>
  );
}

/**
 * Смена бренда — это другая карточка, а не обновление этой: пересоздаём экран
 * целиком. Иначе правки формы одного бренда переживают переход и уезжают в
 * другой — а это чужие данные в поле ввода.
 */
export function BrandCard() {
  const { brandId = '' } = useParams();
  return <BrandScreen key={brandId} brandId={brandId} />;
}

function BrandScreen({ brandId }: { brandId: string }) {
  const access = useAccess();
  const scope = useScope();
  const navigate = useNavigate();
  const stillHere = useStillHere();

  const brand = useLoad<Brand>(`brand:${brandId}`, (signal) =>
    api.get<Brand>(`/brands/${brandId}`, { signal }),
  );

  const [tab, setTab] = useState<Tab>('brand');
  // Форма хранит только правки, а не копию бренда: копию пришлось бы
  // синхронизировать с сервером эффектом, а эффект, переписывающий поле ввода
  // под руками у человека, — это потерянный текст.
  const [edits, setEdits] = useState<Partial<Brand>>({});
  // Недовведённые слова чип-полей. Держит их форма, а не сами поля: «Сохранить»
  // обязано отправить то, что человек видит на экране, включая слово, которое
  // он вписал и не подтвердил Enter-ом. Подробности — `Chips` в `ui/fields`.
  const [regionDraft, setRegionDraft] = useState('');
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [error, setError] = useState<unknown>(null);
  const [archiving, setArchiving] = useState(false);
  const [wasArchived, setWasArchived] = useState(false);
  const [removing, setRemoving] = useState(false);
  /* Отказ удаления живёт отдельно от отказа формы и показывается под шапкой.
     Кнопка стоит в шапке и работает на любой вкладке, а `error` рисуется внутри
     панели «Поправить» — то есть только на первой вкладке и только тому, кто
     может править. Отказ, показанный там, человек бы просто не увидел. */
  const [removeError, setRemoveError] = useState<unknown>(null);

  const readOnly = !access.reaches('PATCH', '/brands/{brandId}');
  const problem = error instanceof ApiError ? error.problem : null;
  const fields = problem ? fieldErrors(problem) : {};

  if (brand.state.kind === 'loading') {
    return (
      <>
        <div className="screen-head">
          <h1>Бренд</h1>
        </div>
        <Skeleton rows={3} />
      </>
    );
  }

  if (brand.state.kind === 'failed') {
    const cause = brand.state.error;
    const notFound = cause instanceof ApiError && cause.problem.status === 404;
    return (
      <>
        <div className="screen-head">
          <h1>Бренд</h1>
        </div>
        {notFound ? (
          <Empty
            eyebrow="Бренд не открылся"
            title="Такого бренда нет"
            action={
              <Link className="btn btn-ghost" to="/brands">
                Ко всем брендам
              </Link>
            }
          >
            {/* Архив отсюда убран, и это не потеря слова: архивный бренд
                открывается этой же карточкой — он никогда и не был причиной
                «не найдено». Причин ровно три, и первая появилась только
                сейчас: бренд удалили, он принадлежит другой организации или в
                адресе опечатка. */}
            Возможно, его удалили, он принадлежит другой организации или в адресе
            опечатка.
          </Empty>
        ) : (
          <ErrorNotice
            error={cause}
            action={
              <button
                className="btn btn-ghost btn-sm"
                type="button"
                onClick={brand.reload}
              >
                Повторить
              </button>
            }
          />
        )}
      </>
    );
  }

  const current = brand.state.data;
  const form = { ...current, ...edits };
  const controls = brandCardControls(current.status, access.reaches);

  async function save() {
    setSaving(true);
    setSaved(false);
    setError(null);
    try {
      const updated = await api.patch<Brand>(`/brands/${brandId}`, {
        name: form.name,
        domain: normalizeDomain(form.domain ?? '') || null,
        regions: chipsValue(form.regions ?? [], regionDraft),
      });
      brand.put(updated);
      setEdits({});
      setRegionDraft('');
      scope.reload();
      setSaved(true);
    } catch (cause) {
      setError(cause);
    } finally {
      setSaving(false);
    }
  }

  /**
   * Архивный бренд не может остаться выбранной областью работы: половина
   * разделов показала бы по нему пустоту без объяснения. Правило одно на
   * архивацию и удаление и живёт в `dropRemovedBrand`: разъехавшись, эти два
   * пути оставили бы область стоять на бренде, которого больше нет.
   *
   * Считается по `scope.liveBrands` ДО перезагрузки области: там ещё лежит
   * список, в котором этот бренд живой, поэтому он и вычитается явно. Между
   * этим вызовом и `scope.reload()` перерисовки не происходит — оба обновления
   * состояния уходят одной пачкой из одного обработчика, — но даже если бы
   * происходила, кадр вышел бы связным: новый выбор при старом списке.
   */
  function dropSelection() {
    dropRemovedBrand(scope, brandId);
  }

  /** Перечитать карточку, не роняя обработчик: не вышло — значит не знаем. */
  async function reread(): Promise<Brand | null> {
    try {
      return await api.get<Brand>(`/brands/${brandId}`);
    } catch {
      return null;
    }
  }

  async function archive() {
    setArchiving(false);
    setSaving(true);
    setError(null);
    try {
      const updated = await api.post<Brand>(`/brands/${brandId}/archive`);
      brand.put(updated);
      dropSelection();
      scope.reload();
    } catch (cause) {
      // Второй щелчок по кнопке или вторая вкладка: сервер отвечает 409.
      // Спрашиваем не ошибку, а сам бренд — `conflictMeansArchived`.
      const conflict = cause instanceof ApiError && cause.problem.status === 409;
      const fresh = conflict ? await reread() : null;

      if (conflictMeansArchived(cause, fresh) && fresh) {
        // Человек хотел ровно того, что уже произошло: красная плашка с
        // отказом пугала бы на пустом месте. Показываем свежую карточку.
        brand.put(fresh);
        setWasArchived(true);
        dropSelection();
        scope.reload();
      } else {
        setError(cause);
      }
    } finally {
      setSaving(false);
    }
  }

  /**
   * Удаление: один запрос, и карточки больше нет.
   *
   * Окно закрывается ПЕРВОЙ строкой — у `Confirm` нет ни `busy`, ни `disabled`,
   * и второй щелчок по той же кнопке ушёл бы вторым запросом. Дальше поток
   * целиком в `removeBrand`: он же решает, что `404` — это успех, и он же
   * уводит на список брендов со строкой о случившемся.
   */
  async function remove() {
    setRemoving(false);
    setSaving(true);
    setRemoveError(null);
    try {
      await removeBrand(
        { id: brandId, name: current.name },
        {
          dropSelection,
          reload: () => scope.reload(),
          // Уходим на список, только если человек всё ещё здесь: ответ приходит
          // позже, и за это время он мог уйти сам.
          navigate: (to, options) => {
            if (stillHere()) navigate(to, options);
          },
        },
      );
    } catch (cause) {
      setRemoveError(cause);
    } finally {
      setSaving(false);
    }
  }

  return (
    <>
      <div className="screen-head">
        <div>
          <h1>{current.name}</h1>
          <p className="stamp">
            {current.domain ? domainProse(current.domain) : 'сайт не добавлен'} ·{' '}
            {brandStatus(current.status)}
            {current.created_at ? ` · заведён ${dateProse(current.created_at)}` : ''}
          </p>
        </div>
        <div className="row-actions">
          {controls.overview ? (
            <button
              className="btn btn-ghost btn-sm"
              type="button"
              onClick={() => {
                scope.selectBrand(current.id);
                navigate('/');
              }}
            >
              Открыть обзор
            </button>
          ) : null}
          {controls.runs ? (
            <button
              className="btn btn-primary btn-sm"
              type="button"
              onClick={() => {
                scope.selectBrand(current.id);
                navigate('/runs');
              }}
            >
              К замерам
            </button>
          ) : null}
          {/* Удаление стоит здесь, а не в панели «Поправить», и это не вкусовое
              размещение: та панель целиком закрыта `readOnly`, о котором
              `brandCardControls` ничего не знает, и вкладка у неё своя. Кнопка,
              спрятанная за двумя чужими условиями, показывалась бы не тем и не
              там, а приёмка на гейте оставалась бы зелёной. */}
          {controls.remove ? (
            <button
              className="btn btn-danger btn-sm"
              type="button"
              disabled={saving}
              onClick={() => setRemoving(true)}
            >
              Удалить бренд
            </button>
          ) : null}
        </div>
      </div>

      {removeError ? <ErrorNotice error={removeError} /> : null}

      {current.status === 'archived' ? (
        <div className="notice warn" role="status">
          <b>Бренд в архиве</b>
          {wasArchived ? 'Он уже был там — показываем свежую карточку. ' : ''}Замеры по
          нему не запускаются, вопросы освободили квоту тарифа, а из переключателя и
          рабочих списков он убран. История осталась и никуда не денется. Вернуть бренд из
          архива нельзя.
        </div>
      ) : null}

      <Tabs<Tab>
        label="Разделы карточки бренда"
        active={tab}
        onChange={setTab}
        tabs={[
          { key: 'brand', title: 'Бренд' },
          { key: 'aliases', title: 'Синонимы названия' },
          { key: 'competitors', title: 'Конкуренты', count: current.competitors_tracked },
        ]}
      />

      {tab === 'brand' ? (
        <TabPanel tab="brand">
          <div className="two-pane">
            <div className="panel">
              <h2 className="panel-h">Что мы про него знаем</h2>
              <ul className="finds">
                <li>
                  <b>
                    {current.domain ? domainProse(current.domain) : 'сайт не добавлен'}
                  </b>
                  <span className="stamp">
                    По нему же считается, ссылаются ли на вас нейросети
                  </span>
                </li>
                <li>
                  <b>
                    {(current.regions ?? []).length > 0
                      ? (current.regions ?? []).join(' · ')
                      : 'города не указаны'}
                  </b>
                  <span className="stamp">География вопросов</span>
                </li>
                <li>
                  <b>
                    {countOf(
                      current.questions_active ?? 0,
                      'вопрос',
                      'вопроса',
                      'вопросов',
                    )}{' '}
                    в ядре
                  </b>
                </li>
              </ul>
              {current.description ? (
                <p className="panel-lead">{current.description}</p>
              ) : null}
              <div className="row-actions">
                <Link className="btn btn-ghost btn-sm" to="/questions">
                  Семантическое ядро
                </Link>
              </div>
            </div>

            {readOnly ? null : (
              <div className="panel">
                <h2 className="panel-h">Поправить</h2>
                {error ? <ErrorNotice error={error} /> : null}
                {saved ? <p className="stamp good">Сохранено</p> : null}
                <form
                  onSubmit={(event) => {
                    event.preventDefault();
                    void save();
                  }}
                >
                  <TextField
                    label="Название"
                    value={form.name}
                    onChange={(name) => setEdits({ ...edits, name })}
                    error={fields.name ?? null}
                    hint="Так, как вас называют покупатели, а не как записано в уставе"
                  />
                  <TextField
                    label="Сайт"
                    value={form.domain ?? ''}
                    onChange={(domain) => setEdits({ ...edits, domain })}
                    onBlur={() =>
                      setEdits({ ...edits, domain: normalizeDomain(form.domain ?? '') })
                    }
                    error={fields.domain ?? null}
                    hint={
                      (form.domain ?? '') !== (current.domain ?? '')
                        ? 'Переезд сайта отметится на ленте событий: без отметки падение видимости выглядит как ваша вина'
                        : 'По сайту пишутся вопросы, и смена домена отмечается на ленте событий бренда'
                    }
                  />
                  {/* Правка карточки сама вопросы не переписывает: уже
                      собранное ядро неизменно на период сравнения. Пересобрать
                      его можно на экране ядра, и там об этом спрашивают. */}
                  <Chips
                    label="Города и регионы"
                    values={form.regions ?? []}
                    onChange={(regions) => setEdits({ ...edits, regions })}
                    draft={regionDraft}
                    onDraftChange={setRegionDraft}
                    placeholder="Казань"
                    hint="Покупатель спрашивает нейросеть про свой город"
                  />
                  <div className="row-actions">
                    <button className="btn btn-primary" type="submit" disabled={saving}>
                      Сохранить
                    </button>
                    {controls.archive ? (
                      <button
                        type="button"
                        className="linkish"
                        disabled={saving}
                        onClick={() => setArchiving(true)}
                      >
                        Архивировать бренд
                      </button>
                    ) : null}
                  </div>
                </form>
              </div>
            )}
          </div>
        </TabPanel>
      ) : null}

      {tab === 'aliases' ? (
        <TabPanel tab="aliases">
          <Aliases brandId={brandId} readOnly={readOnly} />
        </TabPanel>
      ) : null}

      {tab === 'competitors' ? (
        <TabPanel tab="competitors">
          <Competitors brandId={brandId} readOnly={readOnly} />
        </TabPanel>
      ) : null}

      <Confirm
        open={archiving}
        title="Архивировать бренд"
        action="Архивировать бренд"
        danger
        onCancel={() => setArchiving(false)}
        onConfirm={() => void archive()}
      >
        <ArchiveConfirmBody
          name={current.name}
          questions={current.questions_active ?? 0}
        />
      </Confirm>

      <Confirm
        open={removing}
        title="Удалить бренд"
        action="Удалить бренд"
        danger
        onCancel={() => setRemoving(false)}
        onConfirm={() => void remove()}
      >
        <RemoveConfirmBody
          name={current.name}
          status={current.status}
          questions={current.questions_active ?? 0}
        />
      </Confirm>
    </>
  );
}
